import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { apiFetch, ensureCsrf, setCsrfToken } from "@contractor-quoting/api-client";
import { Button, Card, Input, SecondaryButton } from "@contractor-quoting/shared-ui";
import { clearChatDraft } from "../chat-draft.js";
import { clearPendingProjectFormDraft } from "../project-form-draft.js";
import { Shell } from "../components/Shell.js";
import { safeReturnTo } from "../return-to.js";

const validEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export function AuthPage() {
  const location = useLocation();
  const [mode, setMode] = useState<"login" | "register">(
    location.pathname === "/register" ? "register" : "login",
  );
  const [registrationStep, setRegistrationStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [checkingSession, setCheckingSession] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const isProjectClaim = search.get("claim") === "true";
  const isContractorRequest = search.get("returnTo")?.startsWith("/contractor") ?? false;
  const fallback = isContractorRequest ? "/contractor/dashboard" : "/projects";
  const returnTo = isProjectClaim
    ? "/projects?claim=true"
    : safeReturnTo(search.get("returnTo"), fallback);
  const isContractor = !isProjectClaim && returnTo.startsWith("/contractor");
  const registrationReturnTo = isContractor ? "/contractor/dashboard" : returnTo;
  const existingSessionClaim = useRef<Promise<string> | null>(null);

  const claimProject = async () => {
    const claim = await apiFetch<{ projectId: string }>("/v1/auth/claim-anonymous-project", {
      method: "POST",
    });
    return claim.projectId;
  };

  const continueToProject = (projectId: string) => {
    clearChatDraft(projectId);
    clearPendingProjectFormDraft(projectId);
    void navigate(`/projects/${projectId}`);
  };

  useEffect(() => {
    if (!isProjectClaim) return;
    setCheckingSession(true);
    existingSessionClaim.current ??= ensureCsrf().then(claimProject);
    const attempt = existingSessionClaim.current;
    let active = true;
    void attempt
      .then((projectId) => {
        if (active) continueToProject(projectId);
      })
      .catch(() => {
        if (active) setCheckingSession(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const validateRegistration = () => {
    if (!validEmail(email.trim())) return "Enter a valid email address.";
    if (password.length < 12) return "Password must be at least 12 characters.";
    if (password.length > 128) return "Password must be no more than 128 characters.";
    if (password !== confirmPassword) return "Passwords do not match.";
    if (isContractor && businessName.trim().length < 2) return "Enter your business name.";
    return "";
  };

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    if (mode === "register" && registrationStep === "email") {
      if (!validEmail(email.trim())) {
        setError("Enter a valid email address.");
        return;
      }
      setRegistrationStep("password");
      return;
    }
    const validationError = mode === "register" ? validateRegistration() : "";
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    try {
      const endpoint =
        mode === "login"
          ? "/v1/auth/login"
          : `/v1/auth/register${isContractor ? "-contractor" : ""}`;
      const result = await apiFetch<{ csrfToken?: string; pendingVerification?: boolean }>(
        endpoint,
        {
          method: "POST",
          body: JSON.stringify({
            email,
            password,
            ...(mode === "register" && isContractor ? { businessName } : {}),
            ...(mode === "register" ? { returnTo: registrationReturnTo } : {}),
          }),
        },
      );
      if (result.pendingVerification) {
        setPendingVerification(true);
        return;
      }
      if (!result.csrfToken) throw new Error("Authentication session was not created");
      setCsrfToken(result.csrfToken);
      if (search.get("claim") === "true") {
        continueToProject(await claimProject());
      } else void navigate(mode === "register" ? registrationReturnTo : returnTo);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResendMessage("Sending another verification email…");
    try {
      await apiFetch("/v1/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ email, returnTo: registrationReturnTo }),
      });
      setResendMessage("If the account is still pending, a new link will arrive shortly.");
    } catch (reason) {
      setResendMessage(reason instanceof Error ? reason.message : "Resend failed");
    }
  };
  return (
    <Shell>
      <main className="mx-auto max-w-md px-4 py-16">
        <Card className="p-7">
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-800">
            {isProjectClaim
              ? "Your private draft is ready"
              : isContractor
                ? mode === "login"
                  ? "Contractor portal"
                  : "Contractor onboarding"
                : mode === "login"
                  ? "Customer portal"
                  : "Save your project"}
          </p>
          <h1 className="mt-2 text-3xl font-black">
            {isProjectClaim
              ? mode === "login"
                ? "Sign in to save your project"
                : "Create an account to save"
              : isContractor
                ? mode === "login"
                  ? "Contractor login"
                  : "Create a contractor account"
                : mode === "login"
                  ? "Customer login"
                  : "Create a customer account"}
          </h1>
          {isProjectClaim ? (
            <p className="mt-3 text-sm leading-6 text-stone-600">
              Your measurements and project details are already here. Signing in will attach the
              existing draft to your account—nothing needs to be entered again.
            </p>
          ) : null}
          {pendingVerification ? (
            <div className="mt-6 rounded-xl bg-emerald-50 p-4">
              <h2 className="font-black text-emerald-950">Check your email</h2>
              <p className="mt-2 text-sm text-emerald-900">
                We sent a one-time verification link to {email}. It expires in 24 hours.
              </p>
              <button
                className="mt-3 text-sm font-bold underline"
                onClick={() => void resend()}
                type="button"
              >
                Resend verification email
              </button>
              {resendMessage ? <p className="mt-2 text-sm">{resendMessage}</p> : null}
            </div>
          ) : checkingSession ? (
            <p className="mt-6 rounded-xl bg-emerald-50 p-4 text-sm font-semibold text-emerald-900">
              Saving your project to your account…
            </p>
          ) : (
            <form className="mt-6 grid gap-4" onSubmit={(event) => void submit(event)} noValidate>
              {mode === "register" && registrationStep === "password" && isContractor ? (
                <label className="font-semibold">
                  Business name
                  <Input
                    autoComplete="organization"
                    autoFocus
                    required
                    value={businessName}
                    onChange={(event) => setBusinessName(event.target.value)}
                  />
                </label>
              ) : null}
              {mode === "login" || registrationStep === "email" ? (
                <label className="font-semibold">
                  Email
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
              ) : (
                <div className="rounded-xl bg-stone-100 p-3 text-sm">
                  <span className="font-semibold">{email}</span>{" "}
                  <button
                    className="font-bold text-emerald-800 underline"
                    onClick={() => setRegistrationStep("email")}
                    type="button"
                  >
                    Change
                  </button>
                </div>
              )}
              {mode === "login" || registrationStep === "password" ? (
                <label className="font-semibold">
                  Password
                  <Input
                    type="password"
                    autoComplete={mode === "login" ? "current-password" : "new-password"}
                    minLength={mode === "register" ? 12 : undefined}
                    maxLength={128}
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </label>
              ) : null}
              {mode === "register" && registrationStep === "password" ? (
                <label className="font-semibold">
                  Enter new password again
                  <Input
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    maxLength={128}
                    required
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </label>
              ) : null}
              {error ? <p className="rounded-lg bg-red-50 p-3 text-red-800">{error}</p> : null}
              <Button disabled={busy} type="submit">
                {mode === "login"
                  ? isProjectClaim
                    ? "Sign in"
                    : isContractor
                      ? "Contractor login"
                      : "Customer login"
                  : registrationStep === "email"
                    ? "Continue with email"
                    : busy
                      ? "Creating account…"
                      : isContractor
                        ? "Create contractor account"
                        : "Create customer account"}
              </Button>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setError("");
                  const nextMode = mode === "login" ? "register" : "login";
                  setMode(nextMode);
                  setRegistrationStep("email");
                  setPassword("");
                  setConfirmPassword("");
                }}
              >
                {mode === "login"
                  ? `Create a ${isContractor ? "contractor" : "customer"} account`
                  : `I already have a${isContractor ? " contractor" : " customer"} account`}
              </SecondaryButton>
            </form>
          )}
        </Card>
      </main>
    </Shell>
  );
}

export function VerifyEmailPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("Verifying your email…");
  useEffect(() => {
    const token = search.get("token");
    const returnTo = safeReturnTo(search.get("returnTo"));
    if (!token) {
      setMessage("This verification link is missing its token.");
      return;
    }
    void apiFetch<{ csrfToken: string }>("/v1/auth/verify-email", {
      method: "POST",
      body: JSON.stringify({ token }),
    })
      .then(async (result) => {
        setCsrfToken(result.csrfToken);
        setMessage("Your account is verified.");
        const continuation = new URL(returnTo, window.location.origin);
        if (
          continuation.pathname === "/projects" &&
          continuation.searchParams.get("claim") === "true"
        ) {
          const claim = await apiFetch<{ projectId: string }>("/v1/auth/claim-anonymous-project", {
            method: "POST",
          });
          clearChatDraft(claim.projectId);
          clearPendingProjectFormDraft(claim.projectId);
          void navigate(`/projects/${claim.projectId}`, { replace: true });
          return;
        }
        setTimeout(() => void navigate(returnTo, { replace: true }), 800);
      })
      .catch((reason: unknown) =>
        setMessage(reason instanceof Error ? reason.message : "Verification failed"),
      );
  }, []);
  return (
    <Shell>
      <main className="mx-auto max-w-md px-4 py-16">
        <Card>
          <h1 className="text-3xl font-black">Email verification</h1>
          <p className="mt-4 text-stone-600">{message}</p>
        </Card>
      </main>
    </Shell>
  );
}

export function AcceptInvitationPage() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState("Accepting your invitation…");
  useEffect(() => {
    const token = search.get("token");
    if (!token) {
      setMessage("This invitation link is missing its token.");
      return;
    }
    void ensureCsrf()
      .then(() =>
        apiFetch("/v1/invitations/accept", { method: "POST", body: JSON.stringify({ token }) }),
      )
      .then(() => {
        setMessage("Invitation accepted.");
        setTimeout(() => void navigate("/contractor/dashboard", { replace: true }), 800);
      })
      .catch(() => {
        setMessage("Sign in with the invited email address to accept this invitation.");
        setTimeout(
          () =>
            void navigate(
              `/login?returnTo=${encodeURIComponent(`/accept-invitation?token=${token}`)}`,
            ),
          1200,
        );
      });
  }, []);
  return (
    <Shell>
      <main className="mx-auto max-w-md px-4 py-16">
        <Card>
          <h1 className="text-3xl font-black">Staff invitation</h1>
          <p className="mt-4 text-stone-600">{message}</p>
        </Card>
      </main>
    </Shell>
  );
}
